import { useState } from 'react';
import { Check, ChevronRight, Loader2, X } from 'lucide-react';
import { cn } from '@/lib/cn';
import type { ActivityEntry } from '@/state/agentActivityStore';

type Props = {
  entries: ActivityEntry[];
  defaultOpen?: boolean;
};

export function ActivityPanel({ entries, defaultOpen = false }: Props) {
  const [open, setOpen] = useState(defaultOpen);
  if (entries.length === 0) return null;

  const totalMs = entries.reduce((sum, e) => sum + (e.durationMs ?? 0), 0);
  const running = entries.some((e) => e.status === 'running');
  const errored = entries.some((e) => e.status === 'error');

  return (
    <div className="my-2 rounded-md border border-border/60 bg-muted/30">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-1.5 px-2 py-1 text-left text-[11px] text-muted-foreground hover:bg-muted/60"
      >
        <ChevronRight
          className={cn('size-3 transition-transform', open && 'rotate-90')}
        />
        <span className="font-medium">
          Activity · {entries.length} tool{entries.length === 1 ? '' : 's'}
        </span>
        {running ? (
          <Loader2 className="size-3 animate-spin" />
        ) : (
          <span>
            · {(totalMs / 1000).toFixed(2)}s
            {errored && <span className="ml-1 text-destructive">· error</span>}
          </span>
        )}
      </button>
      {open && (
        <ul className="divide-y divide-border/60 px-2 py-1">
          {entries.map((e) => (
            <li
              key={e.id}
              className="flex items-start gap-2 py-1 font-mono text-[11px] leading-relaxed"
            >
              <span className="mt-0.5 shrink-0">
                {e.status === 'running' ? (
                  <Loader2 className="size-3 animate-spin text-muted-foreground" />
                ) : e.status === 'ok' ? (
                  <Check className="size-3 text-emerald-500" />
                ) : (
                  <X className="size-3 text-destructive" />
                )}
              </span>
              <div className="min-w-0 flex-1">
                <div>
                  <span className="font-semibold">{e.tool}</span>
                  {typeof e.durationMs === 'number' && (
                    <span className="ml-2 text-muted-foreground">
                      {e.durationMs}ms
                    </span>
                  )}
                </div>
                {e.inputSummary && (
                  <div className="break-all text-muted-foreground">
                    {e.inputSummary}
                  </div>
                )}
                {e.error && (
                  <div className="break-all text-destructive">{e.error}</div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
